import {
  FormControl,
  FormLabel,
  Input,
  Tag,
  TagLabel,
  TagCloseButton,
  Flex,
  Wrap,
  WrapItem,
  FormErrorMessage,
  FormHelperText,
} from '@chakra-ui/react';
import React, { useState } from 'react';
import {
  FieldPath,
  PathValue,
  Controller,
  useFormContext,
} from 'react-hook-form';

import { StrictFieldValues } from './types';

// This type only allows react-hook-form paths that reference string[] values.
type StringArrayPath<T extends StrictFieldValues> = {
  [K in FieldPath<T>]: PathValue<T, K> extends readonly string[] ? K : never;
}[FieldPath<T>];

type TagInputProps<T extends StrictFieldValues> = {
  name: StringArrayPath<T>;
  label?: string;
  placeholder?: string;
  helperText?: React.ReactNode;
  required?: boolean;
  isDisabled?: boolean;
  tag?: React.ReactNode;
};

/**
 * A tag input component that allows users to enter multiple string values as tags.
 * Users can type a value and press Enter or comma to add it as a tag.
 * Each tag can be removed individually.
 *
 * @template T - The form data type that extends StrictFieldValues
 *
 * Props:
 * - `name`: Field name in the form. Must reference a field typed as string[]
 * - `label`: Optional label displayed above the input
 * - `placeholder`: Optional placeholder text shown in the input
 * - `helperText`: Optional helper text displayed below the input
 * - `required`: When true, field is required and shows validation error if empty
 * - `isDisabled`: When true, the input is disabled and cannot be interacted with
 * - `tag`: Optional, can pass a react component to render as a tag next to the input
 *
 * @example
 * // Basic tag input
 * <TagInput<FormData>
 *   name="keywords"
 *   label="Keywords"
 *   placeholder="Type and press Enter to add tags"
 * />
 *
 * @example
 * // Required tag input with helper text
 * <TagInput<FormData>
 *   name="categories"
 *   label="Categories"
 *   placeholder="Enter categories"
 *   helperText="Add one or more categories by typing and pressing Enter"
 *   required
 * />
 */
export const TagInput = <T extends StrictFieldValues>({
  name,
  label,
  placeholder = 'Type and press Enter to add tags',
  helperText = '',
  required,
  isDisabled = false,
  tag,
}: TagInputProps<T>) => {
  const { control } = useFormContext<T>();
  const [inputValue, setInputValue] = useState('');

  const rules = required
    ? {
        required: { value: true, message: 'This field is required' },
        validate: (value: unknown) => {
          const arrayValue = value as string[];
          return arrayValue && arrayValue.length > 0
            ? true
            : 'At least one tag is required';
        },
      }
    : undefined;

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    onChange: (value: string[]) => void,
    currentValue: string[],
  ) => {
    // add new tag on Enter key press
    if (e.key === 'Enter' && inputValue.trim()) {
      e.preventDefault();
      const trimmedValue = inputValue.trim();
      onChange([...currentValue, trimmedValue]);
      setInputValue('');
    }
  };

  const removeTag = (
    indexToRemove: number,
    onChange: (value: string[]) => void,
    currentValue: string[],
  ) => {
    onChange(currentValue.filter((_, index) => index !== indexToRemove));
  };

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field, fieldState: { error } }) => {
        const currentValue = (field.value as string[]) || [];

        return (
          <FormControl isInvalid={!!error}>
            <Flex alignItems="center">
              {label && <FormLabel fontWeight="bold">{label}</FormLabel>}
              {tag}
            </Flex>

            <Flex
              direction="column"
              border="1px solid"
              borderColor={error ? 'red.500' : 'gray.200'}
              borderRadius="md"
              p={2}
              minHeight="2.5rem"
              _focusWithin={{
                borderColor: error ? 'red.500' : 'blue.500',
                boxShadow: error ? '0 0 0 1px red.500' : '0 0 0 1px blue.500',
              }}
            >
              {currentValue.length > 0 && (
                <Wrap spacing={1} mb={currentValue.length > 0 ? 2 : 0}>
                  {currentValue.map((tagValue, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <WrapItem key={`${tagValue}-${index}`}>
                      <Tag size="sm" variant="solid-muted" colorScheme="blue">
                        <TagLabel>{tagValue}</TagLabel>
                        {!isDisabled && (
                          <TagCloseButton
                            onClick={() =>
                              removeTag(index, field.onChange, currentValue)
                            }
                          />
                        )}
                      </Tag>
                    </WrapItem>
                  ))}
                </Wrap>
              )}

              <Input
                variant="unstyled"
                placeholder={placeholder}
                value={inputValue}
                isDisabled={isDisabled}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={(e) =>
                  handleKeyDown(e, field.onChange, currentValue)
                }
                onBlur={field.onBlur}
                size="sm"
              />
            </Flex>

            <FormErrorMessage>{error?.message}</FormErrorMessage>
            <FormHelperText>{helperText}</FormHelperText>
          </FormControl>
        );
      }}
    />
  );
};
